import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';

import { AgencyClientService, AgencyService, ClientService, OrderService, PermissionService } from '../../../_services';
// JS-1936: AgencyService isn't providedIn:'root', so make the panel self-contained by
// declaring it in @Component providers below. Avoids relying on each host module to add it.

// JS-1936: lightweight row shape for the Minimum break definitions table. Matches the agency
// profile BreakModel 1:1 but kept local to avoid an inter-feature import from agency/profile.
interface ClientBreakRow {
  id: number | null;
  from: number | null;
  to: number | null;
  duration: number | null;
}

// JS-1871 (Self Service 4b) — self-contained panel that renders the "Options" (clock tolerances,
// default order location) and "Order Approval Process" sections. Used from both the client
// settings page (as readonly under .arch-overlay) and the client profile "Settings" tab
// (editable). The two host pages pass in the client payload; the panel handles its own saves.
@Component({
  selector: 'app-client-settings-panel',
  templateUrl: './client-settings-panel.component.html',
  styleUrls: ['./client-settings-panel.component.scss'],
  providers: [MessageService, ConfirmationService, AgencyService],
})
export class ClientSettingsPanelComponent implements OnInit, OnChanges {
  @Input() clientId: number;
  @Input() client: any;
  // JS-1871: agency-side profile owns these settings and should render both sections regardless
  // of the client-portal permissions the panel usually checks.
  @Input() forceVisible = false;
  // JS-1871: on the client settings page the Options block is readonly (source of truth is
  // the client profile Settings tab). Order Approval stays interactive there.
  @Input() readonlyOptions = false;

  public canManageClientOptions = false;
  public canDefineClientValidationProcess = false;
  // JS-1920: Manage Member Roles section visibility. Resolved on init via PermissionService —
  // only users with the 'Manage Member Roles' functionality see the matrix (Q5 answered 2026-10-15).
  public canManageMemberRoles = false;
  // JS-1920 (2026-10-19): per-section edit gates for the 4 Client Settings areas. Each one
  // enables/disables the respective <h2> block on the panel independently. forceVisible stays
  // as an opt-in override (used from the client profile Settings tab where the whole panel
  // mounts regardless of these flags).
  public canEditClientOptions = false;
  public canEditClientEmailNotifications = false;
  // JS-1920 / JS-1936 (2026-10-26, Bleona): canEditClientEmailSender + canEditClientTwilio
  // removed with the per-client SMTP + Twilio surfaces. The matching permissions are being
  // dropped by migration 2026_10_26_120100.
  // JS-1920 (2026-10-19): agency users with Manage Clients override the per-section edit
  // perms (which are client-side only). When true, the 4 sections become editable regardless
  // of the client-side flags above.
  public canManageClients = false;

  // JS-1920 (2026-10-19): per-client Portal Settings (FTE, mandays, NOH). Agency-side only.
  // Loaded when the user is an agency user AND holds Manage Clients. Values null/empty mean
  // the resolver falls back to the agency defaults (which the UI shows as placeholders).
  public portalSettingsFte: number | null = null;
  public portalSettingsMandays: number | null = null;
  public portalSettingsNoh: {
    working_hours: string | null;
    noh_functionality: 0 | 1;
    project_type_id: number | null;
    agency_contact_id: number | null;
    email_template_id: number | null;
    buffer_time: number | null;
    auto_assginment_after_x_mins: number | null;
    auto_accept_mission_requests: 0 | 1;
  } = {
    working_hours: null,
    noh_functionality: 0,
    project_type_id: null,
    agency_contact_id: null,
    email_template_id: null,
    buffer_time: null,
    auto_assginment_after_x_mins: 30,
    auto_accept_mission_requests: 0,
  };
  public agencyFteDefault: number | null = null;
  public agencyMandaysDefault: number | null = null;
  public effectiveFte: number | null = null;
  public effectiveMandays: number | null = null;
  // Agency NOH defaults shown as placeholders / in the "effective" hint on the UI.
  public agencyNohDefault: any = null;
  // True when the current user is NOT a client-role user (agency-side viewer only). Keeps
  // the Portal Settings block hidden from any client user even if canManageClients leaks on.
  public isAgencyViewer = false;

  // JS-1920 (2026-10-19): office-hours-and-days table — same shape the agency widget uses so
  // the serialized `working_hours` JSON is interchangeable between agency and client NOH rows.
  public readonly daysOfWeek: string[] = [
    'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday',
  ];
  public officeHoursTable: { [day: string]: { start: string; end: string; work_day: boolean } } = {};

  // JS-1920 (2026-10-19): dropdown data for the Portal Settings section (project types,
  // project managers / agency contacts, notification templates). Mirrors the agency widget.
  public projectTypes: any[] = [];
  public agencyContacts: any[] = [];
  public agencyTemplates: any[] = [];

  // JS-1920 (2026-10-20): fullscreen dialog that hosts <app-edit-clothing-requirements>
  // with [clientId] set. Only visible when the user clicks the trigger link.
  public clothingDialogOpen = false;

  // JS-1920 (2026-10-21): snapshot of the last persisted Portal Settings payload. Used by
  // savePortalSettings() to skip the PUT + success toast when nothing actually changed.
  // Prevents the "Data saved successfully!" toast from firing on spurious (blur) events —
  // e.g. when focus leaves an input because another dialog (clothing-requirements popup)
  // grabs it. Compared via JSON.stringify of the exact payload the backend receives.
  private portalSettingsSnapshot: string | null = null;

  // JS-1920: per-client Manage Member Roles matrix state.
  // Shape of each entry: { id: number, name: string, permissions: { 'client admin': 0|1, ... } }.
  // `locked` is a list of (functionality_id, role) pairs the UI must render as disabled —
  // backend sends 'client admin' x 'Manage Member Roles' as locked (always-on).
  public memberRolesFunctionalities: Array<{ id: number; name: string; section: string; permissions: { [role: string]: number } }> = [];
  public memberRolesLocked: Array<{ functionality_id: number; role: string }> = [];
  public memberRolesLoading = false;
  public memberRolesSaving = false;
  // JS-1920 (2026-11-04): per-client Self Service toggle. Loaded alongside the matrix.
  // Agency-only — client users never see this (isAgencyViewer gate on the template).
  // When false the SS section rows in the matrix render read-only + visually unticked.
  public isSelfServiceEnabled = false;
  public selfServiceToggleSaving = false;
  public readonly memberRolesColumnRoles: string[] = [
    'client admin',
    'client buyer',
    'client validator',
    'client work validator',
    'client supervisor',
  ];

  public late_clock_tolerance = null;
  public early_clock_tolerance = null;
  public late_clock_in_tolerance = null;
  public late_clock_in_justification_tolerance = null;
  public early_justification_tolerance = null;
  public late_justification_tolerance = null;

  // JS-1936: client-level override for position-alert distance. NULL = inherit from agency.
  // Slider at 0 is persisted as NULL on the backend, mirroring the agency-level control.
  public locationAlertDistance: number | null = null;
  // JS-1920 follow-up (2026-11-02): agency defaults carried in on the client payload (two
  // new fields exposed by AgencyClientController + ClientService). Drive the grey-placeholder
  // display + slider minimum (client can't go below the agency, so OFF is only possible when
  // the agency itself is OFF, i.e. agency distance = 0).
  public agencyLocationAlertDistance = 0;
  public agencyName = '';

  // JS-1936 (2026-10-26, Bleona): per-client assignment-reminder toggle removed. It is an
  // agency-only decision again; the agency profile keeps the single switch.

  // JS-1936 (2026-10-26, Bleona): per-client "Email account for outgoing messages" surface
  // removed. There is no agency fallback for client-triggered sends — if a client hasn't
  // configured SMTP, we don't send. Hiding the surface prevents accidental reliance on an
  // incomplete config.

  // JS-1936 (2026-10-26, Bleona): per-client Twilio Integration surface removed. Client users
  // don't send SMS and there's no agency fallback — same policy as the outgoing-email surface
  // removed above.

  // JS-1936: per-client "Email notifications" recipient lists. Mirrors the agency widget
  // (profile.component.ts :599-601). Each is a string[] of email addresses; backend stores
  // them as a comma-separated string(500) in the matching clients.email_* column. Trigger
  // sites prefer the client's list over the agency's when non-empty.
  public emailJobApplicant: string[] = [];
  public emailSelfBooking: string[] = [];
  public emailMissionAccept: string[] = [];
  public emailMissionCancel: string[] = [];
  // JS-1936: new-pool-employee recipient list is client-only (no agency equivalent) — the
  // backend fires NewPoolEmployeeNotification per new row inserted into client_pool_employees.
  public emailNewPoolEmployees: string[] = [];

  public requireValidaton = 0;
  public numberOfValidators: any = null;
  public totalNumberOfValidators = 0;
  private savedRequireValidation = 0;
  private savedNumberOfValidators: any = null;

  // JS-1936: client-scoped minimum-break ladder. Shape + columns match the agency profile's
  // widget 1:1 so the table rendered by <app-custom-table> is visually identical.
  public breaks: ClientBreakRow[] = [];
  public readonly breaksColumns = [
    { name: 'from (min)', data: 'from', sort: true, type: 'inputNumber' },
    { name: 'to (min)', data: 'to', sort: true, type: 'inputNumber' },
    { name: 'duration (min)', data: 'duration', sort: true, type: 'inputNumber' },
  ];

  constructor(
    private agencyClientService: AgencyClientService,
    private orderService: OrderService,
    private permissionService: PermissionService,
    private messageService: MessageService,
    private translateService: TranslateService,
    private agencyService: AgencyService,
    // JS-1920 (2026-10-19): injected for the per-client Portal Settings load/save.
    private clientService: ClientService,
    private confirmationService: ConfirmationService,
  ) {}

  ngOnInit(): void {
    this.permissionService.canAccessFunctionality('Manage Client Settings').subscribe({
      next: (response) => (this.canManageClientOptions = !!response),
      error: () => (this.canManageClientOptions = false),
    });

    // JS-1920 (2026-10-19): load the 4 per-section edit gates. Each toggles its matching
    // <h2> block independently; forceVisible still unlocks all sections on the client profile
    // Settings tab regardless of these flags.
    this.permissionService.canAccessFunctionality('Edit Client Options').subscribe({
      next: (response) => (this.canEditClientOptions = !!response),
      error: () => (this.canEditClientOptions = false),
    });
    this.permissionService.canAccessFunctionality('Edit Client Email Notifications').subscribe({
      next: (response) => (this.canEditClientEmailNotifications = !!response),
      error: () => (this.canEditClientEmailNotifications = false),
    });
    // JS-1920 / JS-1936 (2026-10-26, Bleona): "Edit Client Email Sender" + "Edit Client
    // Twilio" permission subscriptions removed with the matching UI surfaces.

    // JS-1920 (2026-10-19): Manage Clients is the agency-side override — grants edit on all
    // 4 client-settings sections for agency users regardless of the client-side per-section
    // flags above (which only live in client_permissions and never apply to agency roles).
    this.permissionService.canAccessFunctionality('Manage Clients').subscribe({
      next: (response) => {
        this.canManageClients = !!response;
        // Portal Settings block is only loaded (and only shown) for agency users with the perm.
        if (this.canManageClients && this.isAgencyViewer && this.clientId) {
          this.loadPortalSettings();
          this.loadPortalDropdowns();
        }
      },
      error: () => (this.canManageClients = false),
    });

    // JS-1920 (2026-10-21): seed the snapshot from the sync defaults so savePortalSettings()
    // has a baseline to compare against BEFORE loadPortalSettings() resolves. Otherwise the
    // first (blur) in the window between mount and hydrate would see snapshot=null, treat the
    // state as dirty, and fire a redundant PUT + toast. loadPortalSettings() re-snapshots
    // when the real values arrive.
    this.portalSettingsSnapshot = this.serializePortalSettings();

    // JS-1920 (2026-10-19): detect agency-viewer vs client-role viewer. Portal Settings is
    // agency-side ONLY — never shown to client users even if a perm somehow leaked.
    try {
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
      this.isAgencyViewer = !!(currentUser && typeof currentUser.role === 'string'
        && !currentUser.role.includes('client'));
    } catch {
      this.isAgencyViewer = false;
    }

    // JS-1920 (2026-10-19): seed the office-hours table with placeholders so the inputs
    // always have a defined ngModel target. loadPortalSettings() overwrites with real data.
    this.daysOfWeek.forEach((day) => {
      this.officeHoursTable[day] = { start: '--:--', end: '--:--', work_day: false };
    });

    this.permissionService.canAccessFunctionality('Define Client Validation Process').subscribe((response) => {
      this.canDefineClientValidationProcess = !!response;
      if (this.canDefineClientValidationProcess || this.forceVisible) {
        this.loadValidations();
      }
    });

    // JS-1936: load the client's break-ladder. Backend returns an empty set when the client
    // hasn't defined any — the shift calculator falls back to agency defaults at runtime,
    // but the admin UI deliberately starts empty so it's obvious to the agency which clients
    // are overriding and which are using the agency defaults.
    this.getBreaks();

    // JS-1920: gate the Manage Member Roles matrix behind the functionality permission (Q5);
    // when allowed, load the matrix. clientId may be 0 on the settings page here — the
    // ngOnChanges branch below re-fires loadMemberRoles() the moment it transitions to a
    // real id, same pattern we use for getBreaks().
    this.permissionService.canAccessFunctionality('Manage Member Roles').subscribe({
      next: (response) => {
        this.canManageMemberRoles = !!response;
        if (this.canManageMemberRoles) {
          this.loadMemberRoles();
        }
      },
      error: () => (this.canManageMemberRoles = false),
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.client && this.client) {
      this.late_clock_tolerance = this.client.late_clock_tolerance ?? null;
      this.early_clock_tolerance = this.client.early_clock_tolerance ?? null;
      this.late_clock_in_tolerance = this.client.late_clock_in_tolerance ?? null;
      this.late_clock_in_justification_tolerance = this.client.late_clock_in_justification_tolerance ?? null;
      this.early_justification_tolerance = this.client.early_justification_tolerance ?? null;
      this.late_justification_tolerance = this.client.late_justification_tolerance ?? null;
      // JS-1936: NULL in storage means "inherit from agency" — show 0 on the slider so the
      // user sees "OFF" (same convention as the agency control). Non-null stays as-is.
      this.locationAlertDistance = this.client.location_alert_distance ?? 0;
      // JS-1920 follow-up (2026-11-02): pick up the agency defaults the backend now ships
      // alongside the client payload. Drives the grey placeholder + slider floor.
      this.agencyLocationAlertDistance = Number(this.client.agency_location_alert_distance ?? 0) || 0;
      this.agencyName = this.client.agency_name || '';
      this.emailJobApplicant = this.parseEmailList(this.client.email_job_applicant);
      this.emailSelfBooking = this.parseEmailList(this.client.email_self_booking);
      this.emailMissionAccept = this.parseEmailList(this.client.email_mission_accept);
      this.emailMissionCancel = this.parseEmailList(this.client.email_mission_cancel);
      this.emailNewPoolEmployees = this.parseEmailList(this.client.email_new_pool_employees);
    }

    // JS-1936: on the client settings page clientId starts at 0 and is set only after the
    // async settings fetch resolves (settings-component.component.ts:132). That means
    // ngOnInit's initial getBreaks() call runs with clientId=0 and skips its guard. Re-fetch
    // the moment clientId transitions to a truthy value so the table populates on both hosts.
    if (changes.clientId && this.clientId && !changes.clientId.previousValue) {
      this.getBreaks();
      // JS-1920: the Manage Member Roles matrix needs the same async-id guard; the permission
      // check already fired in ngOnInit but the loadMemberRoles() call there was a no-op if
      // clientId was 0 at that moment. Re-fire now that we have the real id.
      if (this.canManageMemberRoles) {
        this.loadMemberRoles();
      }
      // JS-1920 (2026-10-21): same async-id guard for the Portal Settings block. On the client
      // Settings page clientId starts at 0; the ngOnInit load above was a no-op. Re-fetch so
      // the inputs are hydrated from the backend instead of staying null — otherwise the
      // snapshot stays at the null-defaults and the first legitimate save compares against
      // the wrong baseline.
      if (this.canManageClients && this.isAgencyViewer) {
        this.loadPortalSettings();
        this.loadPortalDropdowns();
      }
    }
  }

  updateTolerance(type: string): void {
    let tolerance = 0;
    switch (type) {
      case 'late_clock_tolerance':
        tolerance = this.late_clock_tolerance; break;
      case 'early_clock_tolerance':
        tolerance = this.early_clock_tolerance; break;
      case 'late_clock_in_tolerance':
        tolerance = this.late_clock_in_tolerance; break;
      case 'late_clock_in_justification_tolerance':
        tolerance = this.late_clock_in_justification_tolerance; break;
      case 'early_justification_tolerance':
        tolerance = this.early_justification_tolerance; break;
      case 'late_justification_tolerance':
        tolerance = this.late_justification_tolerance; break;
    }
    this.agencyClientService.updateClockTolerance({ type, tolerance, client_id: this.clientId }).subscribe(() => {
      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('Success!'),
        detail: this.translateService.instant('Data saved successfully!'),
      });
    });
  }

  // JS-2046: (re)load the stored approval setting. total_validators is the number of
  // Client Approver users and feeds the "not enough approvers" check before saving.
  loadValidations(): void {
    this.orderService.getValidations(this.clientId).subscribe((res) => {
      if (res.status === 'success') {
        this.totalNumberOfValidators = res.total_validators;
        if (res.validations > 0) {
          this.requireValidaton = 1;
          this.numberOfValidators = res.validations == 1 ? '1' : '2';
        } else {
          this.requireValidaton = 0;
          this.numberOfValidators = null;
        }
        this.rememberSavedValidation();
      }
    });
  }

  // JS-2046: snapshot of what is stored, so a refused save snaps the switch and the
  // number back immediately instead of leaving the typed values on screen.
  private rememberSavedValidation(): void {
    this.savedRequireValidation = this.requireValidaton;
    this.savedNumberOfValidators = this.numberOfValidators;
  }

  private resetToSavedValidation(): void {
    this.requireValidaton = this.savedRequireValidation;
    this.numberOfValidators = this.savedNumberOfValidators;
  }

  // JS-2046: the API refuses to store more required approvals than approver users exist;
  // show its message (same i18n key as the pre-save check) instead of a success toast.
  private showValidationError(response: any): void {
    if (response.total_validators !== undefined) {
      this.totalNumberOfValidators = response.total_validators;
    }
    const validatorText = this.totalNumberOfValidators !== 1 ? 'validators' : 'validator';
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant('Error!'),
      detail: this.translateService.instant(response.message, {
        count: this.totalNumberOfValidators,
        validatorText: this.translateService.instant(validatorText),
      }),
    });
  }

  // JS-1936: ng-select tag inputs may hand us either a string[] already or (on first paint
  // after the host sets `client`) a comma-separated string from the response. Normalize to
  // a string[] of trimmed, non-empty emails so the template bindings stay stable.
  private parseEmailList(value: any): string[] {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    return String(value)
      .split(',')
      .map((e: string) => e.trim())
      .filter((e: string) => e.length > 0);
  }

  // JS-1936: per-client "Email notifications" save. One endpoint writes all four lists at
  // once; the backend trigger sites consult the client column first and fall back to the
  // agency's when the client list is empty.
  updateNotificationEmails(): void {
    this.agencyService
      .updateClientNotificationEmails(this.clientId, {
        email_job_applicant: this.emailJobApplicant.join(','),
        email_self_booking: this.emailSelfBooking.join(','),
        email_mission_accept: this.emailMissionAccept.join(','),
        email_mission_cancel: this.emailMissionCancel.join(','),
        email_new_pool_employees: this.emailNewPoolEmployees.join(','),
      })
      .subscribe(
        (response) => {
          if (response?.status === 'success') {
            this.translateService.get(response.message || 'Data saved successfully!').subscribe((res) => {
              this.messageService.add({
                severity: 'success',
                summary: this.translateService.instant('Success!'),
                detail: res,
              });
            });
          }
        },
        () => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('Error!'),
            detail: this.translateService.instant('Could not save changes. Please try again.'),
          });
        },
      );
  }

  // JS-1936 (2026-10-26, Bleona): updateTwilioSettings() removed with the Twilio surface —
  // client users don't configure Twilio from here.

  // JS-1936 (2026-10-26, Bleona): updateEmailInfo() removed with the SMTP surface — client
  // users no longer configure outgoing-email credentials from here.

  // JS-1936 (2026-10-26, Bleona): per-client onAssignmentNotificationChange() removed — the
  // toggle is agency-only again, lives on the agency profile.

  // JS-1920 follow-up (2026-11-02): the effective value shown in the UI. Client's own
  // positive value wins; otherwise the agency's default stands in (including when the
  // agency itself is OFF, in which case 0 falls through and the template renders "OFF").
  get effectiveLocationAlertDistance(): number {
    const own = Number(this.locationAlertDistance);
    if (!isNaN(own) && own > 0) {
      return own;
    }
    return this.agencyLocationAlertDistance;
  }

  // JS-1920 follow-up (2026-11-02): fires in TWO cases, both of which read as "this value
  // equals the agency default":
  //   1. Client has no own override (null / 0)         — classic placeholder, truly inheriting
  //   2. Client's own value happens to equal the agency's — explicit match, still "agency default"
  // Drives the grey text + info icon in the template; the tooltip reads the same either way.
  get showingAgencyLocationFallback(): boolean {
    const own = Number(this.locationAlertDistance);
    if (isNaN(own) || own <= 0) {
      return true;
    }
    return this.agencyLocationAlertDistance > 0 && own === this.agencyLocationAlertDistance;
  }

  // JS-1920 follow-up (2026-11-02): slider floor. The client can only reach OFF (0) when
  // the agency itself is OFF; otherwise 10 m is the lowest selectable value. The agency's
  // own distance DOESN'T raise the floor — the client is free to pick 10 m even if the
  // agency has 100 m (per Bleona's spec).
  get locationSliderMin(): number {
    return this.agencyLocationAlertDistance > 0 ? 10 : 0;
  }

  // JS-1920 follow-up (2026-11-02): slider handle position. When the client hasn't set
  // their own override we visually surface the agency's effective value (so the handle
  // sits where the "50 m" label points), while the storage still records only the client-
  // level override. A drag commits a concrete client value through the setter.
  get locationAlertSliderValue(): number {
    const own = Number(this.locationAlertDistance);
    if (!isNaN(own) && own > 0) {
      return own;
    }
    return this.agencyLocationAlertDistance;
  }
  set locationAlertSliderValue(v: number) {
    // Any drag commits a client-level value; if the user drops the handle back on the
    // agency's exact value we lose the "inherit on agency change" semantics (minor), but
    // the UX is simpler and matches what the slider visibly promises.
    this.locationAlertDistance = v;
  }

  // JS-1920 follow-up (2026-11-02): tooltip label for the info icon next to the grey value.
  get locationFallbackTooltip(): string {
    const name = this.agencyName && this.agencyName.trim() ? this.agencyName.trim() : '';
    return (name ? name + ' ' : '') + 'default';
  }

  // JS-1920 follow-up (2026-11-02): same label used across every portal-settings fallback
  // (FTE, mandays, buffer time, project type, project manager, template). Reads from the
  // same `agencyName` we already carry on the client payload.
  get agencyPortalTooltip(): string {
    return this.locationFallbackTooltip;
  }

  // JS-1920 follow-up (2026-11-02): dropdown placeholders — resolve the agency default's
  // *name* (not id) so the dropdown placeholder tells the user which project type / contact
  // / template the agency has set, matching how the input placeholders surface the agency
  // number for FTE / mandays / buffer time. Returns '' when the agency hasn't set anything.
  get agencyPortalProjectTypeName(): string {
    const id = this.agencyNohDefault && this.agencyNohDefault.project_type_id;
    if (!id || !this.projectTypes || !this.projectTypes.length) {
      return '';
    }
    const row = this.projectTypes.find((t: any) => t && t.id === id);
    return row && row.name ? String(row.name) : '';
  }

  get agencyPortalProjectManagerName(): string {
    const id = this.agencyNohDefault && this.agencyNohDefault.agency_contact_id;
    if (!id || !this.agencyContacts || !this.agencyContacts.length) {
      return '';
    }
    const row = this.agencyContacts.find((c: any) => c && c.id === id);
    if (!row) {
      return '';
    }
    const first = row.first_name || '';
    const last = row.last_name || '';
    return (first + ' ' + last).trim();
  }

  get agencyPortalTemplateName(): string {
    const id = this.agencyNohDefault && this.agencyNohDefault.email_template_id;
    if (!id || !this.agencyTemplates || !this.agencyTemplates.length) {
      return '';
    }
    const row = this.agencyTemplates.find((t: any) => t && t.id === id);
    return row && row.name ? String(row.name) : '';
  }

  // JS-1936: client-level override for position-alert distance. Mirrors the agency control
  // in agency/profile::updateLocationAlertDistance exactly — the backend converts 0/null
  // into NULL, which the resolver reads as "inherit from agency" at this client scope.
  updateLocationAlertDistance(): void {
    const raw = this.locationAlertDistance;
    const payload = raw === null || (raw as any) === '' || isNaN(raw as any) ? null : Number(raw);
    this.agencyService
      .updateLocationAlertDistance(
        { agency_id: this.client?.agency_id, location_alert_distance: payload },
        this.clientId,
      )
      .subscribe(
        () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('Success!'),
            detail: this.translateService.instant('Data saved successfully!'),
          });
        },
        () => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('Error!'),
            detail: this.translateService.instant('Could not save changes. Please try again.'),
          });
        },
      );
  }

  onSwitchChange(event: any): void {
    if (!event) {
      this.numberOfValidators = 0;
      this.requireValidaton = 0;
      this.orderService.updateValidations({ validators: this.numberOfValidators, client_id: this.clientId }).subscribe((response) => {
        if (response.status === 'success') {
          this.rememberSavedValidation();
          const key = this.numberOfValidators === 0
            ? 'Order approval turned off. Amount of approvers automatically set to 0.'
            : response.message;
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('Success!'),
            detail: this.translateService.instant(key),
          });
        } else if (response.status === 'error') {
          this.showValidationError(response);
          this.resetToSavedValidation();
          this.loadValidations();
        }
      });
    }
  }

  updateValidators(value: any): void {
    if (value === 'No') {
      this.requireValidaton = 0;
      this.numberOfValidators = null;
    } else if (value === 'Yes') {
      this.requireValidaton = 1;
      const numValue = typeof this.numberOfValidators === 'string' ? parseInt(this.numberOfValidators, 10) : this.numberOfValidators;
      this.numberOfValidators = numValue && numValue > 0 ? numValue : 1;
    } else {
      const validatorCount = parseInt(value, 10);
      if (isNaN(validatorCount) || validatorCount < 0 || validatorCount > 2) {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('Error!'),
          detail: this.translateService.instant('Validator count must be between 1 and 2'),
        });
        this.requireValidaton = validatorCount > 0 ? 1 : 0;
        return;
      } else if (this.totalNumberOfValidators < validatorCount) {
        const validatorText = this.totalNumberOfValidators !== 1 ? 'validators' : 'validator';
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('Error!'),
          detail: this.translateService.instant('not enough validators', {
            count: this.totalNumberOfValidators,
            validatorText: this.translateService.instant(validatorText),
          }),
        });
        this.resetToSavedValidation();
        return;
      }
      this.numberOfValidators = validatorCount;
      this.requireValidaton = validatorCount > 0 ? 1 : 0;
    }

    this.orderService.updateValidations({ validators: this.numberOfValidators, client_id: this.clientId }).subscribe((response) => {
      if (response.status === 'success') {
        this.rememberSavedValidation();
        if (this.numberOfValidators === 0) {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('Error!'),
            detail: this.translateService.instant('Validator count must be between 1 and 2'),
          });
        } else {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('Success!'),
            detail: this.translateService.instant(response.message),
          });
        }
      } else if (response.status === 'error') {
        this.showValidationError(response);
        this.resetToSavedValidation();
        this.loadValidations();
      }
    });
  }

  validateNumberInput(event: KeyboardEvent): void {
    const allowedKeys = ['0', '1', '2', 'Backspace', 'ArrowLeft', 'ArrowRight'];
    if (!allowedKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

  // JS-1936: break-ladder handlers mirror the agency profile widget (profile.component.ts
  // ~1030-1120). Only differences: fetch/store go through client-scoped service calls, and
  // the row id tracks the single-row persistence model (POST returns the created id so new
  // rows become "persisted" once the first blur completes).

  getBreaks(): void {
    if (!this.clientId) {
      return;
    }
    this.agencyService.getBreaks(this.clientId).subscribe((response) => {
      this.breaks = (response?.agency_breaks || []).map((el: any) => ({
        id: el.id,
        from: el.from,
        to: el.to,
        duration: el.duration,
      }));
    });
  }

  // JS-1920: load the per-client "Manage Member Roles" matrix. Backend returns the current
  // (functionality, role) → allowed map scoped to this client, plus a list of locked cells
  // (currently just 'client admin' x 'Manage Member Roles', which must stay enabled).
  loadMemberRoles(): void {
    if (!this.clientId) {
      return;
    }
    this.memberRolesLoading = true;
    this.agencyService.getClientMemberRoles(this.clientId).subscribe(
      (res) => {
        this.memberRolesLoading = false;
        this.memberRolesFunctionalities = (res?.member_roles?.functionalities || []).map((f: any) => ({
          id: f.id,
          name: f.name,
          // JS-1920 (2026-10-15): section tag drives the subtitle grouping (Orders /
          // Self Service / Other). Defaults to 'other' if the backend hasn't tagged
          // the row — avoids an untagged row silently breaking the subtitle loop.
          section: f.section || 'other',
          permissions: { ...(f.permissions || {}) },
        }));
        this.memberRolesLocked = (res?.member_roles?.locked || []).map((l: any) => ({
          functionality_id: l.functionality_id,
          role: l.role,
        }));
        // JS-1920 (2026-11-04): same endpoint carries the SS toggle state.
        this.isSelfServiceEnabled = !!res?.is_self_service_enabled;
      },
      () => {
        this.memberRolesLoading = false;
      },
    );
  }

  // JS-1920 (2026-11-04): Self Service toggle handler. The user confirms the flip, then we
  // POST — the backend rewrites the SS + Orders matrix sections with the matching defaults.
  // We reload the matrix so the UI reflects the new values; the Other section stays put.
  // Hidden from client users on the template level (*ngIf="isAgencyViewer").
  onToggleSelfService(nextValue: boolean): void {
    if (!this.clientId || this.selfServiceToggleSaving) {
      return;
    }
    // Snapshot the pre-flip value so we can roll back the ngModel if the user cancels or
    // the save fails — p-inputSwitch already mutated the bound property by the time we
    // land here.
    const previousValue = !nextValue;
    const confirmKey = nextValue
      ? 'confirmation-self-service-toggle-on'
      : 'confirmation-self-service-toggle-off';
    this.confirmationService.confirm({
      header: this.translateService.instant('Confirmation'),
      message: this.translateService.instant(confirmKey),
      accept: () => {
        this.selfServiceToggleSaving = true;
        this.agencyService.toggleClientSelfService(this.clientId, nextValue).subscribe(
          (res) => {
            this.selfServiceToggleSaving = false;
            this.isSelfServiceEnabled = !!res?.is_self_service_enabled;
            // Matrix defaults for both sections were rewritten — pull them back in.
            this.loadMemberRoles();
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('Success!'),
              detail: this.translateService.instant('Data saved successfully!'),
            });
          },
          () => {
            this.selfServiceToggleSaving = false;
            this.isSelfServiceEnabled = previousValue;
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('Error!'),
              detail: this.translateService.instant('Could not save changes. Please try again.'),
            });
          },
        );
      },
      reject: () => {
        this.isSelfServiceEnabled = previousValue;
      },
    });
  }

  // JS-1920 (2026-11-04): tri-use predicate for the Self Service section of the matrix.
  // When the toggle is OFF the SS rows render read-only and visually unticked. Used by the
  // template to both disable the checkboxes and hide the ticked state.
  isSelfServiceSectionDisabled(fcn: { section?: string }): boolean {
    return fcn?.section === 'self service' && !this.isSelfServiceEnabled;
  }

  // JS-1920: persist the full matrix on every toggle (autosave, mirroring the agency
  // widget's updatePermissions pattern). Backend upserts into client_permissions and
  // enforces the client admin x Manage Member Roles lock defensively regardless of what
  // we send. We deliberately do NOT reload after save — a reload per toggle would race
  // with fast clicking and overwrite in-flight local state; the lock is already enforced
  // in the UI via [disabled] + isMemberRoleLocked, and any DB drift self-heals on the
  // next client-profile open via syncClientPermissions.
  saveMemberRoles(): void {
    if (!this.clientId) {
      return;
    }
    this.memberRolesSaving = true;
    const payload = {
      functionalities: this.memberRolesFunctionalities.map((f) => ({
        id: f.id,
        permissions: { ...f.permissions },
      })),
    };
    this.agencyService.saveClientMemberRoles(this.clientId, payload).subscribe(
      () => {
        this.memberRolesSaving = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('Success!'),
          detail: this.translateService.instant('Data saved successfully!'),
        });
      },
      () => {
        this.memberRolesSaving = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('Error!'),
          detail: this.translateService.instant('Could not save changes. Please try again.'),
        });
      },
    );
  }

  // JS-1920: UI helper — true when this (functionality, role) cell must be disabled.
  isMemberRoleLocked(functionalityId: number, role: string): boolean {
    return this.memberRolesLocked.some(
      (l) => l.functionality_id === functionalityId && l.role === role,
    );
  }

  // JS-1920 (2026-10-15): Self-Service ↔ Orders mutual exclusivity, generalised.
  // Enabling ANY row in one section when ANY row in the other section is already
  // enabled for the same role triggers a confirmation and (on accept) unticks every
  // conflicting row in the opposite section. Rejecting the dialog reverts the row
  // the user just toggled.
  //
  // Why section-based: it uses the `section` tag the backend attaches to each row
  // (see ClientService::CLIENT_FUNCTIONALITY_SECTIONS). If Bleona later moves a row
  // between Orders and Self Service, no frontend edit is needed — the backend map
  // is the single source of truth. The agency widget only compared 'Self Service'
  // against six hardcoded order names; this generalises that symmetry to every row
  // in either section.
  //
  // All five columns on this matrix are client roles, so unlike the agency version
  // there is no clientRoles.includes(role) gate — every role is a client role by
  // construction.
  // JS-1920 (2026-10-15): per-direction confirmation message. Picks the key that
  // names the section the user is enabling AND the section getting disabled, so the
  // confirmation copy matches the user's intent instead of always sounding like a
  // Self-Service-triggered warning.
  private readonly MUTUALLY_EXCLUSIVE_SECTIONS: Readonly<Record<string, { opposing: string; message: string }>> = {
    'self service': {
      opposing: 'orders',
      message: 'self service disables order privileges message',
    },
    orders: {
      opposing: 'self service',
      message: 'orders disables self service privileges message',
    },
  };

  onToggleMemberRole(
    isChecked: boolean | number,
    fcn: { id: number; name: string; section: string; permissions: { [role: string]: number } },
    role: string,
  ): void {
    // ngModel on a checkbox emits boolean; the backing store uses 0/1. Normalise.
    const checked = Boolean(isChecked);

    // JS-1920 follow-up (2026-11-02): paired-permission behaviour ported from the agency
    // Manage Member Roles widget (profile.component.ts:onTogglePermission). The agency
    // widget auto-enables View Order when Create Order is checked and auto-disables Create
    // Order when View Order is unchecked. When this matrix moved to the client-settings
    // panel the coupling was lost — restoring it here.
    if (fcn.name === 'Create Order' && checked) {
      const viewOrder = this.memberRolesFunctionalities.find((f) => f.name === 'View Order');
      if (viewOrder && !viewOrder.permissions[role]) {
        viewOrder.permissions[role] = 1;
      }
    } else if (fcn.name === 'View Order' && !checked) {
      const createOrder = this.memberRolesFunctionalities.find((f) => f.name === 'Create Order');
      if (createOrder && createOrder.permissions[role]) {
        createOrder.permissions[role] = 0;
      }
    }

    // JS-1920 follow-up (2026-11-02): enabling "Edit Templates" also enables "View Own
    // Self Service Projects" for the same role. Without that prereq the client user never
    // reaches a self-service project screen (project-planning / project-publishing), so
    // the Edit Templates perm would be inert on its own. The tooltip calls this out
    // ("Requires at least 'view own self service projects' permission"); auto-enabling
    // keeps the UX consistent with the agency widget's Create Order → View Order pattern.
    // Reverse direction: unticking "View Own Self Service Projects" also unticks "Edit
    // Templates" — the coupling is symmetric, same as Create Order ↔ View Order.
    if (fcn.name === 'Edit Templates' && checked) {
      const viewOwnSelfService = this.memberRolesFunctionalities.find(
        (f) => f.name === 'View Own Self Service Projects',
      );
      if (viewOwnSelfService && !viewOwnSelfService.permissions[role]) {
        viewOwnSelfService.permissions[role] = 1;
      }
    } else if (fcn.name === 'View Own Self Service Projects' && !checked) {
      const editTemplates = this.memberRolesFunctionalities.find((f) => f.name === 'Edit Templates');
      if (editTemplates && editTemplates.permissions[role]) {
        editTemplates.permissions[role] = 0;
      }
    }

    const exclusion = this.MUTUALLY_EXCLUSIVE_SECTIONS[fcn.section];

    if (checked && exclusion) {
      const conflicting = this.memberRolesFunctionalities.filter(
        (row) => row.section === exclusion.opposing && row.permissions[role],
      );

      if (conflicting.length === 0) {
        this.saveMemberRoles();
        return;
      }

      this.confirmationService.confirm({
        header: this.translateService.instant('Confirmation'),
        message: this.translateService.instant(exclusion.message),
        accept: () => {
          conflicting.forEach((row) => {
            row.permissions[role] = 0;
          });
          this.saveMemberRoles();
        },
        reject: () => {
          // Revert the just-toggled checkbox back off. setTimeout lets ngModel
          // finish its current cycle before we flip the value again.
          setTimeout(() => {
            fcn.permissions[role] = 0;
          });
        },
      });
      return;
    }

    // JS-1920 follow-up (2026-11-02): disabling "Supervisor On Site" (a.k.a. Timestamp
    // Manager) checks the backend for users of this role who are currently assigned as
    // TM on at least one project — same safeguard the agency widget runs. If users exist,
    // show the long confirmation so the admin knows those users will lose TM access on
    // their projects; reject reverts the checkbox back to ticked. Fail-open on request
    // error (don't block the admin from toggling if the probe itself fails).
    if (fcn.name === 'Supervisor On Site' && !checked) {
      const agencyId =
        (this.client && this.client.agency_id) ||
        JSON.parse(localStorage.getItem('currentUser') || 'null')?.agency_id;
      if (agencyId) {
        this.agencyService.isTMRoleSelectedOnProject({ role, agency_id: agencyId }).subscribe(
          (response) => {
            if (response && response.has_users) {
              this.confirmationService.confirm({
                header: this.translateService.instant('Confirmation'),
                message: this.translateService.instant('A TM with this role is selected on at least one project, do you really want to remove this privilege?'),
                accept: () => this.saveMemberRoles(),
                reject: () => {
                  setTimeout(() => {
                    fcn.permissions[role] = 1;
                  });
                },
              });
            } else {
              this.saveMemberRoles();
            }
          },
          () => this.saveMemberRoles(),
        );
        return;
      }
    }

    this.saveMemberRoles();
  }

  addBreak(): void {
    // JS-1936: seed `from` so the row is actually persistable on first blur.
    // - First row of the ladder starts at 0 (the (0, to] band).
    // - Subsequent rows start at previous.to + 1 so the half-open intervals never overlap.
    // - If the previous row has no `to` (unbounded above), stop and tell the user — appending
    //   another row would mean every save gets rejected by the overlap check.
    const last = this.breaks.length > 0 ? this.breaks[this.breaks.length - 1] : null;
    if (last && (last.to === null || last.to === undefined)) {
      this.messageService.add({
        severity: 'info',
        summary: this.translateService.instant('Information'),
        detail: this.translateService.instant('cannot add after unbounded break'),
      });
      return;
    }
    const nextFrom = last ? (last.to as number) + 1 : 0;
    this.breaks.push({ id: null, from: nextFrom, to: null, duration: null });
  }

  // JS-1936: capture the row's prior state before any store attempt so an overlap error
  // from the backend can roll the user's input back cleanly. The agency widget doesn't do
  // this today, which means an invalid blur silently keeps the bogus value on screen.
  private snapshotRow(index: number): ClientBreakRow | null {
    const row = this.breaks[index];
    return row ? { id: row.id, from: row.from, to: row.to, duration: row.duration } : null;
  }

  private revertRow(index: number, snapshot: ClientBreakRow | null): void {
    if (!snapshot || !this.breaks[index]) {
      return;
    }
    this.breaks[index].id = snapshot.id;
    this.breaks[index].from = snapshot.from;
    this.breaks[index].to = snapshot.to;
    this.breaks[index].duration = snapshot.duration;
    // Rebuild the array reference so <app-custom-table> notices the revert.
    this.breaks = [...this.breaks];
  }

  private handleBreakResponse(response: any, index: number, snapshot: ClientBreakRow | null): void {
    if (response?.status === 'error') {
      this.revertRow(index, snapshot);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('Error!'),
        detail: this.translateService.instant(response.message || 'This range overlaps an existing minimum break definition.'),
      });
      return;
    }
    if (response?.agency_break?.id) {
      this.breaks[index].id = response.agency_break.id;
    }
  }

  storeBreak(index: number): void {
    const row = this.breaks[index];
    if (row.id !== null && row.from !== null && row.duration === null) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('Error!'),
        detail: this.translateService.instant('Duration is mandatory!'),
      });
    }
    if (row.from !== null && row.duration !== null) {
      const snapshot = this.snapshotRow(index);
      this.agencyService.storeBreak(row, this.clientId).subscribe((response) => {
        this.handleBreakResponse(response, index, snapshot);
      });
    }
  }

  syncWithpPreviousTo(index: number): void {
    if (index > 0) {
      const previousIndex = index - 1;
      const snapshot = this.snapshotRow(previousIndex);
      this.breaks[previousIndex].to = this.breaks[index].from;
      this.agencyService.storeBreak(this.breaks[previousIndex], this.clientId).subscribe((response) => {
        this.handleBreakResponse(response, previousIndex, snapshot);
      });
    }
  }

  removeBreak(id: number | null): void {
    this.translateService.get('are you sure, you want to delete this minimum break definition?').subscribe((res) => {
      this.confirmationService.confirm({
        header: this.translateService.instant('Confirmation'),
        message: res,
        accept: () => {
          if (!id) {
            // Row that was never persisted (addBreak appends at most one placeholder with
            // id=null at a time) — drop every unsaved row from the local collection. The
            // previous filter kept rows where any field was non-null, which meant the user
            // could never delete the last row once they'd started typing into it.
            this.breaks = this.breaks.filter((b) => b.id !== null);
            return;
          }
          this.agencyService.removeBreak(id).subscribe(() => {
            this.breaks = this.breaks.filter((b) => b.id !== id);
          });
        },
      });
    });
  }

  // JS-1920 (2026-10-19): per-client Portal Settings load + save. The backend returns the
  // client's overrides, the agency defaults, and the effective values. We populate the UI
  // state from the overrides (null → empty input → agency fallback at render time).
  loadPortalSettings(): void {
    if (!this.clientId) {
      return;
    }
    this.clientService.getPortalSettings(this.clientId).subscribe(
      (response) => {
        this.portalSettingsFte = response?.client?.fte ?? null;
        this.portalSettingsMandays = response?.client?.mandays ?? null;
        const nohRow = response?.client_noh ?? null;
        if (nohRow) {
          this.portalSettingsNoh = {
            working_hours: nohRow.working_hours ?? null,
            noh_functionality: (nohRow.noh_functionality ? 1 : 0) as 0 | 1,
            project_type_id: nohRow.project_type_id ?? null,
            agency_contact_id: nohRow.agency_contact_id ?? null,
            email_template_id: nohRow.email_template_id ?? null,
            buffer_time: nohRow.buffer_time ?? null,
            auto_assginment_after_x_mins: nohRow.auto_assginment_after_x_mins ?? 30,
            auto_accept_mission_requests: (nohRow.auto_accept_mission_requests ? 1 : 0) as 0 | 1,
          };
        }
        this.agencyNohDefault = response?.agency_noh ?? null;
        this.agencyFteDefault = response?.agency?.fte ?? null;
        this.agencyMandaysDefault = response?.agency?.mandays ?? null;
        this.effectiveFte = response?.effective_fte ?? null;
        this.effectiveMandays = response?.effective_mandays ?? null;

        // JS-1920 (2026-10-19): hydrate the office-hours-and-days table from working_hours
        // JSON (client row first, agency fallback for display when no client override).
        const workingHoursJson = nohRow?.working_hours ?? this.agencyNohDefault?.working_hours ?? null;
        if (workingHoursJson) {
          try {
            const parsed = JSON.parse(workingHoursJson);
            this.daysOfWeek.forEach((day) => {
              if (parsed[day]) {
                this.officeHoursTable[day] = {
                  start: parsed[day].start ?? '--:--',
                  end: parsed[day].end ?? '--:--',
                  work_day: !!parsed[day].work_day,
                };
              }
            });
          } catch {
            // Malformed JSON — leave placeholders intact.
          }
        }

        // JS-1920 (2026-10-21): snapshot the hydrated state so savePortalSettings() can
        // skip the PUT + toast on spurious (blur) events where nothing actually changed.
        this.portalSettingsSnapshot = this.serializePortalSettings();
      },
      () => {
        // Silent fail — backend returns 403 for users without Manage Clients or outside
        // the agency scope. The UI simply doesn't render the block in that case.
      },
    );
  }

  // JS-1920 (2026-10-21): canonical serialization used ONLY for dirty-detection. Each field
  // is normalized to a stable primitive type first so that quirks like PrimeNG's inputSwitch
  // flipping 0 ↔ false, ng-select emitting undefined vs null, or text inputs returning a
  // string "60" vs the backend's number 60 don't register as "dirty" when the user hasn't
  // actually changed anything. The POST body is still built from the raw values below.
  private serializePortalSettings(): string {
    const toNumOrNull = (v: unknown): number | null => {
      if (v === null || v === undefined || v === '') return null;
      const num = Number(v);
      return Number.isFinite(num) ? num : null;
    };
    const toBit = (v: unknown): 0 | 1 => {
      return (v === true || v === 1 || v === '1' || v === 'true') ? 1 : 0;
    };
    const toStrOrNull = (v: unknown): string | null => {
      return (v === null || v === undefined || v === '') ? null : String(v);
    };
    const n = this.portalSettingsNoh || ({} as any);
    return JSON.stringify({
      fte: toNumOrNull(this.portalSettingsFte),
      mandays: toNumOrNull(this.portalSettingsMandays),
      noh: {
        working_hours: toStrOrNull(n.working_hours),
        noh_functionality: toBit(n.noh_functionality),
        project_type_id: toNumOrNull(n.project_type_id),
        agency_contact_id: toNumOrNull(n.agency_contact_id),
        email_template_id: toNumOrNull(n.email_template_id),
        buffer_time: toNumOrNull(n.buffer_time),
        auto_assginment_after_x_mins: toNumOrNull(n.auto_assginment_after_x_mins),
        auto_accept_mission_requests: toBit(n.auto_accept_mission_requests),
      },
    });
  }

  savePortalSettings(): void {
    if (!this.clientId) {
      return;
    }
    // JS-1920 (2026-10-21): dirty-check. (blur) handlers on the Portal Settings inputs fire
    // whenever focus leaves — including when another dialog (e.g. the clothing-requirements
    // popup, or an ng-select panel) grabs focus. Without this guard, every such blur emitted
    // a redundant PUT and a "Data saved successfully!" toast even when the user hadn't
    // changed anything. Compare against the snapshot taken at load / after the last save.
    const serialized = this.serializePortalSettings();
    if (serialized === this.portalSettingsSnapshot) {
      return;
    }
    this.clientService.savePortalSettings(this.clientId, {
      fte: this.portalSettingsFte,
      mandays: this.portalSettingsMandays,
      noh: this.portalSettingsNoh,
    }).subscribe(
      () => {
        this.portalSettingsSnapshot = serialized;
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('Success!'),
          detail: this.translateService.instant('Data saved successfully!'),
        });
      },
      () => {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('Error!'),
          detail: this.translateService.instant('Could not save changes. Please try again.'),
        });
      },
    );
  }

  // JS-1920 (2026-10-19): serialize the office-hours table to the noh.working_hours JSON
  // string and persist via the shared PUT. Normalises partial "0800" → "08:00" first so the
  // backend never stores an invalid HH:mm value. Mirrors the agency setOfficeHours shape 1:1.
  saveOfficeHours(): void {
    for (const day of this.daysOfWeek) {
      const row = this.officeHoursTable[day];
      if (row.start && row.start.length === 4) {
        row.start = `${row.start.substr(0, 2)}:${row.start.substr(2, 2)}`;
      }
      if (row.end && row.end.length === 4) {
        row.end = `${row.end.substr(0, 2)}:${row.end.substr(2, 2)}`;
      }
    }
    this.portalSettingsNoh.working_hours = JSON.stringify(this.officeHoursTable);
    this.savePortalSettings();
  }

  // JS-1920 (2026-10-19): dropdown data for the Portal Settings section. agencyService is
  // already injected for the breaks ladder; reusing it here for project types, agency contacts,
  // and notification templates — same methods the agency widget uses so the shapes match.
  loadPortalDropdowns(): void {
    const currentUser = (() => {
      try { return JSON.parse(localStorage.getItem('currentUser') || 'null'); }
      catch { return null; }
    })();
    const agencyId = currentUser?.agency_id ?? null;

    this.agencyService.getProjectTypes().subscribe((res: any) => {
      this.projectTypes = res || [];
    });
    if (agencyId) {
      this.agencyService.getAgencyContacts(agencyId).subscribe((res: any) => {
        this.agencyContacts = res || [];
      });
    }
    this.agencyService.loadTemplates('notification').subscribe((res: any) => {
      this.agencyTemplates = res?.templates || [];
    });
  }

  // Copy of agency maskTimeInput — formats raw digit entry (0800) into HH:mm as the user types.
  maskTimeInput(event: any): void {
    const input = event.target;
    let value = (input.value || '').replace(/\D/g, '');
    if (value.length > 5) {
      value = value.substring(0, 5);
    }
    if (value.length >= 4) {
      input.value = `${value.substr(0, 2)}:${value.substr(2, 2)}`;
    } else {
      input.value = value;
    }
  }
}
